import { V as VividElement, a as attr, o as observable, h as html, d as createRegisterFunction, f as defineVividComponent } from './vivid-element.js';
import { A as AccordionItem, a as accordionItemDefinition } from './definition.js';
import { d as keyEnd, g as keyHome, f as keyArrowDown, e as keyArrowUp } from './key-codes.js';
import { w as wrapInBounds } from './numbers.js';
import { s as slotted, e as elements } from './slotted.js';
import { c as classNames } from './class-names.js';

const styles = ".base{display:flex;box-sizing:border-box;flex-direction:column}::slotted(:not(:only-of-type)){border-bottom:1px solid var(--vvd-color-neutral-200)}";

var __defProp = Object.defineProperty;
var __decorateClass = (decorators, target, key, kind) => {
  var result = void 0 ;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (decorator(target, key, result) ) || result;
  if (result) __defProp(target, key, result);
  return result;
};
const AccordionExpandMode$1 = {
  /**
   * Designates only a single AccordionItem can be open a time.
   */
  single: "single",
  /**
   * Designates multiple AccordionItems can be open simultaneously.
   */
  multi: "multi"
};
class Accordion extends VividElement {
  constructor() {
    super(...arguments);
    /* eslint-disable-next-line @nrwl/nx/workspace/no-attribute-default-value */
    this.expandmode = AccordionExpandMode$1.single;
    this.activeItemIndex = 0;
    this.change = () => {
      this.$emit("change", this.activeid);
    };
    this.setItems = () => {
      if (this.accordionItems.length === 0) {
        return;
      }
      this.accordionIds = this.getItemIds();
      this.activeid = this.accordionIds[this.activeItemIndex];
      this.accordionItems.forEach((item, index) => {
        if (item instanceof AccordionItem) {
          item.addEventListener("change", this.activeItemChange);
          if (this.isSingleExpandMode()) {
            const expandedItem = this.findExpandedItem();
            if (expandedItem === null && index === 0) {
              item.expanded = true;
            } else {
              item !== this.findExpandedItem() ? item.expanded = false : item.expanded = true;
            }
          }
        }
        const itemId = this.accordionIds[index];
        item.setAttribute(
          "id",
          typeof itemId !== "string" ? `accordion-${index + 1}` : itemId
        );
        item.addEventListener("keydown", this.handleItemKeyDown);
      });
      if (this.isSingleExpandMode()) {
        const expandedItem = this.findExpandedItem() ?? this.accordionItems[0];
        expandedItem.setAttribute("aria-disabled", "true");
      }
    };
    this.removeItemListeners = (oldValue) => {
      oldValue.forEach((item) => {
        item.removeEventListener("change", this.activeItemChange);
        item.removeEventListener("keydown", this.handleItemKeyDown);
      });
    };
    this.activeItemChange = (event) => {
      if (event.defaultPrevented || event.target !== event.currentTarget) {
        return;
      }
      event.preventDefault();
      const selectedItem = event.target;
      this.activeid = selectedItem.getAttribute("id");
      if (this.isSingleExpandMode()) {
        this.resetItems();
        selectedItem.expanded = true;
        selectedItem.setAttribute("aria-disabled", "true");
        this.accordionItems.forEach((item) => {
          if (!item.hasAttribute("disabled") && item.id !== this.activeid) {
            item.removeAttribute("aria-disabled");
          }
        });
      }
      this.activeItemIndex = Array.from(this.accordionItems).indexOf(
        selectedItem
      );
      this.change();
    };
    this.handleItemKeyDown = (event) => {
      if (event.target !== event.currentTarget) {
        return;
      }
      this.accordionIds = this.getItemIds();
      switch (event.key) {
        case keyArrowUp:
          event.preventDefault();
          this.adjust(event.target, -1);
          break;
        case keyArrowDown:
          event.preventDefault();
          this.adjust(event.target, 1);
          break;
        case keyHome:
          this.focusItem(0);
          break;
        case keyEnd:
          this.focusItem(this.accordionItems.length - 1);
          break;
      }
    };
  }
  /**
   * @internal
   */
  accordionItemsChanged(oldValue) {
    if (this.$fastController.isConnected) {
      this.removeItemListeners(oldValue);
      this.setItems();
    }
  }
  findExpandedItem() {
    for (let item = 0; item < this.accordionItems.length; item++) {
      if (this.accordionItems[item].hasAttribute("expanded") === true) {
        return this.accordionItems[item];
      }
    }
    return null;
  }
  resetItems() {
    this.accordionItems.forEach((item) => {
      item.expanded = false;
    });
  }
  getItemIds() {
    return this.accordionItems.map((accordionItem) => {
      return accordionItem.getAttribute("id");
    });
  }
  isSingleExpandMode() {
    return this.expandmode !== AccordionExpandMode$1.multi;
  }
  adjust(item, adjustment) {
    this.focusItem(
      wrapInBounds(
        0,
        this.accordionItems.length - 1,
        this.accordionItems.indexOf(item) + adjustment
      )
    );
  }
  focusItem(index) {
    const element = this.accordionItems[index];
    if (element instanceof AccordionItem) {
      element.expandbutton.focus();
    }
  }
  closeAll() {
    if (this.expandmode === AccordionExpandMode$1.multi) {
      this.accordionItems.forEach((item) => {
        item.expanded = false;
      });
    }
  }
}
__decorateClass([
  attr({ attribute: "expand-mode" })
], Accordion.prototype, "expandmode");
// @ts-expect-error Type is incorrectly non-optional
__decorateClass([
  observable
], Accordion.prototype, "accordionItems");

const getClasses = (_) => classNames("base");
const AccordionTemplate = html`
	<div class="${getClasses}">
		<slot ${slotted({ property: "accordionItems", filter: elements() })}></slot>
	</div>
`;

const AccordionExpandMode = {
  single: "single",
  multi: "multi"
};
const accordionDefinition = defineVividComponent(
  "accordion",
  Accordion,
  AccordionTemplate,
  [accordionItemDefinition],
  {
    styles
  }
);
const registerAccordion = createRegisterFunction(accordionDefinition);

export { AccordionExpandMode as A, accordionDefinition as a, Accordion as b, registerAccordion as r };
